import { Text, Link, DesignLanguage, useIsDesignLanguage, useDesignLanguagePerClass } from '@afterpaytouch/core'
import clsx from 'clsx'
import React, { FunctionComponent, useEffect } from 'react'
import { TrackOnMount, useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import {
  setCreditCheckAgree,
  useAppDispatch,
  useCheckoutState,
  setTermsDisclosureAgree,
  openPPAModal,
  useIsAfterpayFinanceFeeEligible,
  setConfirmTermsViewed,
  useIsBillingAgreementApprovalProcess,
} from '../../state'
import { TermLink } from '../TermLink'
import styles from './style.module.css'
import { useGetTerms, useLateFeeDetails, useTermsEventProps, useIsUkRegulatedTerms } from './helper'
import { MaskedElement } from '@afterpaytouch/integrations/MaskedElement'
import { Trans, useTranslation } from '../../utils/convergence'
import { PaymentAuthorizationLink } from '../PaymentAuthorizationLink'

export const Terms: FunctionComponent = () => {
  const { t } = useTranslation()
  const { instrument, logEvent } = useMonitoringWithEnduringEventProperties()
  const { lateFee } = useLateFeeDetails()
  const { started } = useCheckoutState()
  const terms = useGetTerms()
  const termsEventProps = useTermsEventProps()
  const dispatch = useAppDispatch()
  const isAFF = useIsAfterpayFinanceFeeEligible()
  const isBillingAgreementApprovalProcess = useIsBillingAgreementApprovalProcess()
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const designLanguageStyles = useDesignLanguagePerClass(styles)
  const linkColor = isArcadeDesignLanguage ? 'Black' : 'Orca'
  const isUkRegulated = useIsUkRegulatedTerms()

  const onInstallmentAgreementClick = (): void => {
    if (isBillingAgreementApprovalProcess) {
      return
    }

    dispatch(openPPAModal())
  }

  useEffect(() => {
    dispatch(setCreditCheckAgree(termsEventProps.creditCheckConsent))
    dispatch(setTermsDisclosureAgree(termsEventProps.termsDisclosureAgree))
  }, [termsEventProps.creditCheckConsent, termsEventProps.termsDisclosureAgree])

  useEffect(() => {
    dispatch(setConfirmTermsViewed(terms))
  }, [terms])

  if (isAFF) {
    return null
  }

  const components = {
    terms: (
      <TermLink
        key='summary-terms-of-service'
        href={t('common:termsOfServiceUrl')}
        color={linkColor}
        fontFace='CashSansMono'
        onClick={() => logEvent(TrackingEvent.PRESSED_SUMMARY_PAGE_TERMS_LINK, { outboundLink: t('common:termsOfServiceUrl') })}
      />
    ),
    privacyPolicy: (
      <TermLink
        key='summary-privacy-policy'
        href={t('common:privacyPolicyUrl')}
        color={linkColor}
        fontFace='CashSansMono'
        onClick={() => logEvent(TrackingEvent.PRESSED_SUMMARY_PAGE_PRIVACY_POLICY_LINK, { outboundLink: t('common:privacyPolicyUrl') })}
      />
    ),
    installmentAgreement: (
      <Link
        target={isBillingAgreementApprovalProcess ? '_blank' : undefined}
        href={isBillingAgreementApprovalProcess ? t('common:termsOfInstalmentAgreementUrl') : undefined}
        kind='Underline'
        color={linkColor}
        fontFace='CashSansMono'
        testNameSpace='ppa-link-installment'
        key='summary-instalment-agreement'
        onClick={instrument(TrackingEvent.PRESSED_SUMMARY_INSTALMENT_AGREEMENT_LINK, onInstallmentAgreementClick)}
      />
    ),
    paymentAuthorization: <PaymentAuthorizationLink color={linkColor} />,
    additionalProductInformation: (
      <TermLink key='summary-additional-product-information' href={t('common:additionalProductInformationUrl')} color={linkColor} fontFace='CashSansMono' />
    ),
    cardAccountAgreement: (
      <Link
        kind='Underline'
        color={linkColor}
        fontFace='CashSansMono'
        testNameSpace='card-account-agreement'
        key='summary-card-account-agreement'
        target='_blank'
        href={t('summary:terms.cardAccountAgreementUrl')}
      />
    ),
  }

  const values = {
    lateFee,
    otbAmount: started?.otbAmount?.amount,
  }

  return (
    <>
      <div className={clsx(designLanguageStyles.wrapper, isUkRegulated && styles.ukRegulatedWrapper)}>
        <MaskedElement actionName='Terms And Conditions'>
          <Text size='XXS' color='Gray40' testNameSpace='terms-conditions' fontFace='CashSansMono'>
            <Trans i18nKey={terms} components={components} values={values} />
          </Text>
        </MaskedElement>
        <TrackOnMount eventName={TrackingEvent.VIEWED_CHECKOUT_SUMMARY_TERMS} eventProperties={{ ...termsEventProps, termsViewed: terms }} />
      </div>
    </>
  )
}
